import React, { useEffect, useState, useCallback } from 'react';
import { Card } from '../../components/styled/Card';
import styles from '../interview/interview-home/interview-home.module.css';
import { useAppStore } from '../../../src/store/appStore';
import { AuthorizedMeeting } from 'api-types';
import { useNavigate } from 'react-router-dom';
import { Box, Button, CardActions, CardContent, Divider, Typography } from '@mui/joy';
import { ErrorCard } from '../../components/ErrorHandling';

interface MeetingHomeProps {
  title?: string;
  viewPath?: (meetingId: string) => string;
  showUsage?: boolean;
}

export default function MeetingHome({
  title = 'Your Assistants',
  viewPath,
  showUsage = true,
}: MeetingHomeProps) {
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [meetings, setMeetings] = useState<AuthorizedMeeting[] | null>(null);

  const fetchAuthorizedMeetings = useAppStore(state => state.fetchAuthorizedMeetings);

  const loadMeetings = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await fetchAuthorizedMeetings();
      setMeetings(response.meetings ?? []);
    } catch (err) {
      setError('Failed to load your authorized meetings. Please try again later.');
      setMeetings(null);
    } finally {
      setIsLoading(false);
    }
  }, [fetchAuthorizedMeetings]);

  useEffect(() => {
    loadMeetings();
  }, [loadMeetings]);

  const handleMeetingClick = useCallback(
    (meetingId: string) => {
      if (viewPath) {
        navigate(viewPath(meetingId));
      } else {
        navigate(`/meeting-report/${meetingId}/1`);
      }
    },
    [navigate, viewPath]
  );

  const handleUsageClick = useCallback(
    (meetingId: string) => {
      navigate(`/meeting-usage/${meetingId}`);
    },
    [navigate]
  );

  return (
    <div>
      <Box display={'flex'} justifyContent={'space-between'}>
        <Typography level="title-lg">{title}</Typography>
      </Box>
      <Divider sx={{ marginTop: 2, marginBottom: 2 }} />

      {isLoading && (
        <div className="flex flex-col items-center justify-center py-12">
          <div className="w-5 h-5 border-2 border-gray-300 border-t-gray-600 rounded-full animate-spin"></div>
          <p className="text-gray-600 mt-2 text-sm">Loading your assistants...</p>
        </div>
      )}

      {error && (
        <ErrorCard
          title="Unable to load assistants"
          message={error}
          onRetry={loadMeetings}
          isRetrying={isLoading}
          showBackButton={false}
          className="mb-4"
        />
      )}

      {!isLoading && !error && (
        <Box display={'flex'} gap={2} flexWrap={'wrap'}>
          {!meetings || meetings.length === 0 ? (
            <div className="w-full text-center py-8 border border-gray-200 rounded">
              <p className="text-gray-600">No assistants found</p>
            </div>
          ) : (
            meetings.map((meeting: AuthorizedMeeting) => {
              const meetingId = meeting.id || '';
              return (
                <Card
                  key={meetingId}
                  className={styles.card}
                  onClick={() => handleMeetingClick(meetingId)}
                >
                  <CardContent>
                    <Typography level="title-md">{meeting.name}</Typography>
                    <Typography level="body-sm">{meeting.description}</Typography>
                  </CardContent>
                  <CardActions sx={{ gap: '8px', justifyContent: 'space-between' }}>
                    {showUsage && (
                      <Button
                        variant="plain"
                        color="neutral"
                        size="sm"
                        onClick={(e: any) => {
                          e.stopPropagation();
                          e.preventDefault();
                          handleUsageClick(meetingId);
                        }}
                      >
                        Usage
                      </Button>
                    )}
                    <Button
                      variant="plain"
                      color="neutral"
                      size="sm"
                      onClick={(e: any) => {
                        e.stopPropagation();
                        e.preventDefault();
                        handleMeetingClick(meetingId);
                      }}
                    >
                      View
                    </Button>
                  </CardActions>
                </Card>
              );
            })
          )}
        </Box>
      )}
    </div>
  );
}
